import { useMemo, memo, useState, useEffect, useCallback } from 'react'
import { useSelector } from 'react-redux'

import { MultiLineDnd } from 'common-components'
import type { IDndItem } from 'common-components/MultiLineDND/types'
import { TDndListItems } from 'common-components/DndList/types'
import TrackItem from './TrackItem'

import { dispatch } from 'store'
import { editorThunks } from 'store/features/editor/thunks'
import { editorSelectors } from 'store/features/editor/selectors'
import { projectDataThunks } from 'store/features/projectData/thunks'
import { projectDataSelectors } from 'store/features/projectData/selectors'

import { IMediaItem } from 'types/media'
// import { PROJECT_MEDIA_TYPES } from 'consts'
import { pixelsToSeconds, secondsToPixels } from 'utils/timeline'
import { isTextMediaItem } from 'utils'
import {
  getDistinctZIndicesDescending,
  zIndexToVisualRowRank,
  visualRowRankToZIndex
} from 'utils/timelineZIndex'
import { buildHeightByZIndexForVisualRows } from './utils'
import { useTrackHeight } from 'hooks/useTrackHeight'
import { useItemClickHandler } from '../hooks/use-item-click-handler'
import { useTimelineContext } from 'context/TimelineContext'

import { TIMELINE_CONTENT_WRAPPER_ID } from '../../consts'
import { AUDIO_TRACK_HEIGHT } from '../AudioTracks/consts'
import { COLORS } from 'consts/colors'
import * as Styled from './styled'

const MEDIA_COLORS = {
  text: { selectedBorderColor: COLORS.secondarySemiDark },
  default: { selectedBorderColor: COLORS.primaryExtraDark }
}

interface IVisualMediaTracksProps {
  preventOnClick: boolean
}

const VisualMediaTracks = ({ preventOnClick }: IVisualMediaTracksProps) => {
  const mediaItemsFromStore = useSelector(projectDataSelectors.mediaItems)
  const timelineRulerSliceDuration = useSelector(
    editorSelectors.timelineRulerSliceDuration
  )
  const activeTimelineItems = useSelector(editorSelectors.activeTimelineItems)
  const sameLineMultipleActiveMediaIds = useSelector(
    projectDataSelectors.sameLineMultipleActiveMediaIds
  )

  const [mediaItems, setMediaItems] =
    useState<IMediaItem[]>(mediaItemsFromStore)
  const { previewMediaItems, dropState } = useTimelineContext()
  const activeItems = previewMediaItems ?? mediaItems

  const handleItemClick = useItemClickHandler()
  const trackHeight = useTrackHeight()

  useEffect(() => {
    setMediaItems(mediaItemsFromStore)
  }, [mediaItemsFromStore])

  const heightByZIndex = useMemo(
    () =>
      buildHeightByZIndexForVisualRows(
        mediaItems,
        trackHeight,
        AUDIO_TRACK_HEIGHT
      ),
    [mediaItems, trackHeight]
  )

  const resolveDropLineStyle = useCallback(
    (_: number, lineItems: IDndItem[]) => {
      const firstItem =
        lineItems[0] && mediaItems.find(m => m.id === lineItems[0].id)
      const height = firstItem
        ? (heightByZIndex.get(firstItem.zIndex) ?? trackHeight)
        : trackHeight
      return { height }
    },
    [mediaItems, heightByZIndex, trackHeight]
  )

  const handleItemsUpdate = (updatedItems: TDndListItems) => {
    const rowCount =
      updatedItems.length === 0
        ? 1
        : Math.max(...updatedItems.map(item => item.lineIndex), 0) + 1

    const updatedMediaItems = updatedItems
      .map(updatedItem => {
        const original = mediaItems.find(item => item.id === updatedItem.id)
        if (!original) return null

        return {
          ...original,
          zIndex: visualRowRankToZIndex(updatedItem.lineIndex, rowCount),
          startTime: pixelsToSeconds(
            updatedItem.left,
            timelineRulerSliceDuration
          ),
          duration: pixelsToSeconds(
            updatedItem.width,
            timelineRulerSliceDuration
          )
        }
      })
      .filter((item): item is IMediaItem => item !== null)

    setMediaItems(updatedMediaItems)
    dispatch(projectDataThunks.setMediaItems({ value: updatedMediaItems }))
  }

  const handleDragStart = () =>
    dispatch(editorThunks.setIsTimelineItemDragging(true))

  const handleDragEnd = (updatedItems: TDndListItems) => {
    handleItemsUpdate(updatedItems)
    dispatch(editorThunks.setIsTimelineItemDragging(false))
  }

  const dndData = useMemo(() => {
    const orderedZs = getDistinctZIndicesDescending(activeItems)
    const isMultiSelection = activeTimelineItems.length > 1

    return activeItems.map(item => {
      const { id, startTime, duration, zIndex, type } = item

      const isActive = activeTimelineItems.some(
        ai => ai.id === id && ai.type === type
      )
      const isText = isTextMediaItem(type)
      const colorConfig = isText ? MEDIA_COLORS.text : MEDIA_COLORS.default

      // logic: DND-kit handles same-line groups
      // vanilla DOM handles cross-line
      const isInSameLineGroup = sameLineMultipleActiveMediaIds.includes(id)
      const isDraggingDisabled =
        isActive && isMultiSelection && !isInSameLineGroup

      return {
        id,
        left: secondsToPixels(startTime, timelineRulerSliceDuration),
        width: secondsToPixels(duration, timelineRulerSliceDuration),
        lineIndex: zIndexToVisualRowRank(zIndex, orderedZs),
        isDraggingDisabled,
        content: (
          <TrackItem
            data={item}
            isActive={isActive}
            onClick={event =>
              !preventOnClick && handleItemClick({ id, type, event, isActive })
            }
            updateMediaItems={setMediaItems}
            isResizeBtnsVisible={!isMultiSelection}
            selectedBorderColor={colorConfig.selectedBorderColor}
            selectedBorderSize={4}
            trackRowHeight={heightByZIndex.get(zIndex) ?? trackHeight}
          />
        )
      }
    })
  }, [
    activeItems,
    timelineRulerSliceDuration,
    activeTimelineItems,
    sameLineMultipleActiveMediaIds,
    preventOnClick,
    heightByZIndex,
    trackHeight,
    handleItemClick
  ])

  return (
    <Styled.VisualMediaTracks>
      <MultiLineDnd
        data={dndData}
        selectedIds={sameLineMultipleActiveMediaIds}
        dropLineSettings={{
          style: { height: trackHeight },
          className: 'visual-media-track'
        }}
        resolveDropLineStyle={resolveDropLineStyle}
        isMultiLineAllowed
        onDragEnd={handleDragEnd}
        onDragStart={handleDragStart}
        boundingContainerId={TIMELINE_CONTENT_WRAPPER_ID}
        dropState={dropState}
      />
    </Styled.VisualMediaTracks>
  )
}

export default memo(VisualMediaTracks)
